import { accountMcpSchema } from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { accountKeys } from "./queries.ts";

/**
 * ESI's MCP server in account settings (mcp-server-core D8): its URL and how to add it to a
 * harness. The harness then signs in through ESI and shows up under Connected apps.
 */
export function McpServer() {
  const { t } = useTranslation();
  const mcp = useQuery({
    queryKey: accountKeys.mcp,
    queryFn: async () => accountMcpSchema.parse(await ok(await api.api.account.mcp.$get())),
  });
  if (mcp.isPending) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (mcp.isError) {
    return <Notice tone="danger">{mcp.error.message || t("errors.network")}</Notice>;
  }
  const { url, enabled } = mcp.data;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm leading-relaxed text-fg-secondary">{t("mcpServer.lead")}</p>
      {enabled ? null : <Notice>{t("mcpServer.disabled")}</Notice>}
      <ul className="flex flex-col divide-y divide-line rounded-lg border border-line bg-surface-raised">
        <Line label={t("mcpServer.url")} value={url} />
        <Line
          label={t("mcpServer.claudeCode")}
          value={`claude mcp add --transport http esi ${url}`}
        />
        <Line label={t("mcpServer.vscode")} value={url} hint={t("mcpServer.vscodeHint")} />
      </ul>
      <p className="text-xs text-fg-muted">{t("mcpServer.signIn")}</p>
    </div>
  );
}

/** One copyable line: a label, the value in code, a Copy button that confirms once. */
function Line(props: { label: string; value: string; hint?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(props.value);
      setCopied(true);
    } catch {
      // No clipboard (permissions, insecure context): the value stays selectable.
    }
  }
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-xs font-medium text-fg-secondary">{props.label}</span>
        <div className="min-w-0">
          <code className="block select-all break-all font-mono text-[13px] text-fg">
            {props.value}
          </code>
          {props.hint ? <p className="mt-1 text-xs text-fg-muted">{props.hint}</p> : null}
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-label={`${t("mcpServer.copy")}: ${props.label}`}
        onClick={() => void copy()}
      >
        {copied ? (
          <Check aria-hidden="true" className="size-3.5" strokeWidth={2} />
        ) : (
          <Copy aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        )}
        {copied ? t("mcpServer.copied") : t("mcpServer.copy")}
      </Button>
    </li>
  );
}
